<script lang="ts">
	import { onMount } from 'svelte';
	import { format } from 'svelte-i18n';
	//Design system components
	import { Alert, Button, ListItem, Loader, Modal, Stack, Text } from '@Shelly/shelly-design-system';
	//Services
	import { rpc_over_ble_android } from 'src/services/native_container_requests_android';
	import { rpc_over_ble_ios } from 'src/services/native_container_requests_ios';

	//Stores
	import { user_data } from 'src/stores/account/user';
import { os, wifi_ssids } from 'src/stores/session/native-bridge';
	import { saved_wifi_neworks } from 'src/stores/features/wifi/passwords';
	import { mobile } from 'src/services/mobile-rework';
	import { isWallDisplay } from 'src/helpers/interface_helpers';

	export let inclusionData;
	export let isScanOpen = false;

	const isIOS = $os === 'IOS';
	const defaultMaxShowed = 9;
	let maxShowedNetworks = defaultMaxShowed;
	let showMore = false;
	let showModal = false;
	let waitForResponse = false;
	let networksListError = '';

	let wifiList = [];

	const getAvailableNetworks = async () => {
		wifi_ssids.set([]);
		if (isIOS) {
			rpc_over_ble_ios(inclusionData.selected.id, 'scanWiFi');
			inclusionData.callback = () => rpc_over_ble_ios(inclusionData.selected.id, 'scanWiFi');
		} else {
			rpc_over_ble_android(inclusionData.selected.mac, 'scanWiFi');
			inclusionData.callback = () => rpc_over_ble_android(inclusionData.selected.mac, 'scanWiFi');
		}
	};

	const formatNetworksList = (networks) => {
		const checkDuplicateList = [];
		const networksList = [];

		networks.forEach((network) => {
			if (checkDuplicateList.indexOf(network.ssid) == -1) {
				const icon = `fa-wifi${network.rssi < -67 ? (network.rssi < -80 ? '-weak' : '-fair') : ''}`;

				networksList.push({
					value: network.ssid,
					title: network.ssid + (network.auth == 0 ? ' (open)' : ' '),
					is_open: network.auth == 0,
					icon,
				});
				checkDuplicateList.push(network.ssid);
			}
		});
		return networksList;
	};

	const resetShowMore = () => {
		showMore = false;
		maxShowedNetworks = defaultMaxShowed;
	};

	const onSelect = (networkItem) => {
		showModal = false;
		isScanOpen = false;
		resetShowMore();

		if (
			inclusionData.wifi.ssid !== networkItem.value &&
			$saved_wifi_neworks &&
			$saved_wifi_neworks[$user_data?.email]?.ssid !== networkItem.value
		) {
			inclusionData.wifi.pass = '';
		}

		inclusionData.wifi.ssid = networkItem.value;
	};

	const handleInclusionState = (e: CustomEvent) => {
		const parsed_data = JSON.parse(e.detail[0]);

		if (parsed_data?.status === 'wifiListReceived') {
			wifiList = parsed_data?.list;
		}
	};

	onMount(() => {
		mobile.addEventListener('including_device', handleInclusionState);
		return () => {
			mobile.removeEventListener('including_device', handleInclusionState);
		};
	});

	const closeModalOnAndroidBack = (e: CustomEvent) => {
		if (showModal) {
			e.preventDefault();
			showModal = false;
			isScanOpen = false;
		}
	};

	onMount(() => {
		if (mobile.isMobile) {
			mobile.addEventListener('back_button', closeModalOnAndroidBack);
			return () => mobile.removeEventListener('back_button', closeModalOnAndroidBack);
		}
	});
</script>

{#if !isWallDisplay(inclusionData.selected.model)}
	<Button
		size="m"
		width="full"
		variant="secondary"
		class="mb-s"
		on:click={() => {
			showModal = true;
			isScanOpen = true;
			getAvailableNetworks();
		}}
		loading={waitForResponse || inclusionData.bluetooth.connecting}
	>
		{$format('scan_for_wifi_networks', {
			default: 'Scan for Wi-Fi networks',
		})}
	</Button>
{/if}

{#if showModal}
	<Modal
		on:close={() => {
			showModal = false;
			isScanOpen = false;
			resetShowMore();
		}}
		showModal={showModal && isScanOpen}
		backdropClose={true}
		title={$format('available_networks', { default: 'Available networks' })}
	>
		<svelte:fragment slot="content">
			{#if wifiList.length <= 0}
				<div class="d-flex justify-content-center align-items-center flex-direction-column">
					<Loader class="mb-xs" />
					<Text styleType="controls-small" class="text-center text-bold">
						{$format('title_scanning', {
							default: 'Scanning ...',
						})}
					</Text>
				</div>
			{:else if wifiList.length > 0}
				<Stack gap="s">
					{#each formatNetworksList(wifiList) as networkItem, i}
						{#if i < maxShowedNetworks}
							<ListItem
								viewType="fullColored"
								listItemBgColor="--color-1"
								icon={'fa-duotone ' + networkItem.icon}
								actionBtn
								title={networkItem.title}
								on:click={() => onSelect(networkItem)}
							/>
						{/if}
					{/each}

					{#if !showMore}
						<ListItem
							viewType="more"
							listItemBgColor="--color-1"
							title={$format('show_more_network_items', {
								values: {
									networkItems: wifiList.length - maxShowedNetworks,
								},
								default: `+${wifiList.length - maxShowedNetworks} ${$format('more', {
									default: 'more',
								})}`,
							})}
							actionBtn
							on:click={() => {
								showMore = true;
								maxShowedNetworks = wifiList.length;
							}}
						/>
					{/if}
				</Stack>
			{:else}
				<Alert type="danger" heading={networksListError} class="mb-s" />
				<Button
					size="m"
					class="width-full mr-s text-capitalized"
					responsiveSize={{
						sm: 'm',
					}}
					variant="primary"
					on:click={getAvailableNetworks}
				>
					{$format('button_rescan', { default: 'Rescan' })}
				</Button>
			{/if}
		</svelte:fragment>
	</Modal>
{/if}
